/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import * as React from "react";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalButton,
} from "baseui/modal";
import { StyledBody } from "baseui/card";
import { LabelSmall, LabelMedium } from "baseui/typography";
import { LOGO_URL } from "./Utils/SiteLogo.mjs";
import { useDexterTheme } from "./DesignSystem/Theme/ThemeContext";
import { PLACEMENT, StatefulTooltip } from "baseui/tooltip";
import { gql, useQuery } from "@apollo/client";
import { formattedDateTime } from "./Api/ApiSerialization";
import appInfo from "./Api/Gql/AppInfo";
import { AppInfo, MigrationExecutionState } from "Api";
import {
  DEXTER_DESCRIPTION,
  DATA_RIGHTS_LEGEND,
} from "./Reference/Documentation/DocumentationMeta";
import { getTooltipOverrides } from "./DesignSystem/TooltipStyles";

const AppInfoTooltip = ({
  appInfoValues,
}: {
  appInfoValues: AppInfo;
}): JSX.Element => {
  const { currentTheme } = useDexterTheme();
  return (
    <LabelSmall color={currentTheme.colors.tooltipText}>
      Build Date: {appInfoValues.version.buildDate}
      <br />
      Commit: {appInfoValues.version.abbreviatedCommitHash}
      <MigrationStatus
        migrationState={appInfoValues.schemaStatus.lastExecutedMigration}
      />
    </LabelSmall>
  );
};

const AppVersionInfo = ({
  appInfoValues,
}: {
  appInfoValues?: AppInfo;
}): JSX.Element | null => {
  const { currentTheme } = useDexterTheme();
  if (!appInfoValues) {
    return null;
  }
  return (
    <StatefulTooltip
      placement={PLACEMENT.top}
      content={<AppInfoTooltip appInfoValues={appInfoValues} />}
      overrides={getTooltipOverrides(currentTheme)}
    >
      <LabelMedium color={currentTheme.colors.text}>
        &nbsp; {appInfoValues.version.descriptor}
      </LabelMedium>
    </StatefulTooltip>
  );
};

const MigrationStatus = ({
  migrationState,
}: {
  migrationState: MigrationExecutionState;
}): JSX.Element => {
  switch (migrationState.__typename) {
    case "ExecutedMigration":
      return (
        <div style={{ whiteSpace: "pre" }}>
          {`Latest Data Migration: {\n` +
            `    Id: ${migrationState.changeId}\n` +
            `    Executed: ${
              formattedDateTime(migrationState.timestamp) ?? "N/A"
            }\n}`}
        </div>
      );
    case "NoMigrationsExecuted":
      return <div>{migrationState.message}</div>;
    case "ErrorGettingMigrations":
      return <div>{migrationState.errorMessage}</div>;
    default:
      const _exhaustiveCheck: never = migrationState;
      return _exhaustiveCheck;
  }
};

export const AboutModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}): JSX.Element => {
  const { currentTheme } = useDexterTheme();

  // "skip" keeps this off the critical path until the user opens the modal.
  const { data } = useQuery(gql(appInfo), { skip: !isOpen });
  const appInfoValues = data?.appInfo as AppInfo | undefined;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeable
      animate
      autoFocus
      overrides={{
        Dialog: {
          style: {
            backgroundColor: currentTheme.colors.pageBackgroundColor,
          },
        },
      }}
    >
      <ModalHeader
        style={{
          backgroundColor: currentTheme.colors.pageBackgroundColor,
          color: currentTheme.colors.defaultTextColor,
          fontFamily: currentTheme.fonts.primary,
          fontSize: currentTheme.fontSizes.large,
        }}
      >
        About
      </ModalHeader>
      <ModalBody>
        <StyledBody>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              marginBottom: "16px",
              gap: "8px",
            }}
          >
            <img
              src={LOGO_URL.toString()}
              alt="Logo"
              style={{ width: "200px", height: "auto" }}
            />
            <AppVersionInfo appInfoValues={appInfoValues} />
          </div>

          <div style={{ marginBottom: "16px" }}>
            <p
              style={{
                fontFamily: currentTheme.fonts.primary,
                fontSize: currentTheme.fontSizes.medium,
                color: currentTheme.colors.text,
              }}
            >
              {DEXTER_DESCRIPTION}
            </p>

            <p
              style={{
                fontFamily: currentTheme.fonts.primary,
                fontSize: "0.75rem",
                lineHeight: 1.5,
                color: currentTheme.colors.text,
                margin: 0,
              }}
            >
              {DATA_RIGHTS_LEGEND}
            </p>
          </div>
        </StyledBody>
      </ModalBody>
      <ModalFooter>
        <div
          style={{
            display: "flex",
            gap: "8px",
            justifyContent: "flex-end",
          }}
        >
          <ModalButton
            kind="secondary"
            onClick={onClose}
            overrides={{
              BaseButton: {
                style: {
                  fontFamily: currentTheme.fonts.primary,
                  fontSize: currentTheme.fontSizes.medium,
                  backgroundColor: "transparent",
                  color: currentTheme.colors.text,
                },
              },
            }}
          >
            Close
          </ModalButton>
        </div>
      </ModalFooter>
    </Modal>
  );
};
